import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  CloudArrowUp, FloppyDisk, Plus, Trash, ArrowsClockwise, ArrowLeft,
  CheckCircle, XCircle, WarningCircle, FileText, Eye,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

// Editable text fields shown in the approval form (key -> label). Grouped for layout.
// The "Values" window is rendered separately (VALUE_ROWS) since it mixes editable amounts
// with computed INR columns.
const GROUPS = [
  { title: "Shipping Bill", fields: [["shipping_bill_no", "SB No."], ["shipping_bill_date", "SB Date"], ["port_code", "Port Code"]] },
  { title: "Exporter", fields: [["exporter_name", "Exporter Name"], ["iec_number", "IEC Number"], ["currency", "Currency"]] },
  { title: "Invoices", fields: [["export_invoice_number", "Export Invoice No. (from ERP)"], ["export_invoice_date", "Export Invoice Date"], ["gst_invoice_number", "GST Invoice Number"], ["gst_invoice_date", "GST Invoice Date"], ["contract_no", "Contract No. & Dt"]] },
];

// "Values" window. type: "fc" = editable foreign-currency amount; "amt" = editable INR amount;
// "inr" = read-only, computed as fields[from] × exchange_rate.
const VALUE_ROWS = [
  { k: "invoice_value_fc", lbl: "Invoice Value (FC)", type: "fc" },
  { k: "invoice_value_inr", lbl: "Invoice Value INR", type: "inr", from: "invoice_value_fc" },
  { k: "igst_value", lbl: "IGST Value", type: "amt" },
  { k: "igst_amount", lbl: "IGST AMT", type: "amt" },
  { k: "freight_fc", lbl: "Freight (FC)", type: "fc" },
  { k: "freight_inr", lbl: "Freight INR", type: "inr", from: "freight_fc" },
  { k: "insurance_fc", lbl: "Insurance (FC)", type: "fc" },
  { k: "insurance_inr", lbl: "Insurance INR", type: "inr", from: "insurance_fc" },
  { k: "packing_fc", lbl: "Packing (FC) · Surcharge", type: "fc" },
  { k: "packing_inr", lbl: "Packing INR · Surcharge", type: "inr", from: "packing_fc" },
];
const VALUE_EXTRA = [
  { k: "fob_value_fc", lbl: "FOB Value (FC)", type: "fc" },
  { k: "dbk_claim", lbl: "DBK Claim", type: "amt" },
  { k: "rodtep_amount", lbl: "RODTEP Amount", type: "amt" },
];

const _num = (v) => { const n = parseFloat(String(v ?? "").replace(/,/g, "")); return Number.isFinite(n) ? n : NaN; };
// Computed INR = FC × exchange rate; blank when either input is missing.
const inrFromFc = (fc, rate) => {
  const f = _num(fc), r = _num(rate);
  if (!Number.isFinite(f) || !Number.isFinite(r)) return "";
  return (f * r).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const cardStyle = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, padding: 16, boxShadow: "0 1px 2px rgba(2,8,23,0.04)" };
const labelStyle = { fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4, display: "block" };
const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 13, background: "#fff" };

function StatusPill({ status }) {
  const map = {
    mapped: { bg: "#DCFCE7", fg: "#166534", Icon: CheckCircle, label: "Mapped" },
    partial: { bg: "#FEF3C7", fg: "#92400E", Icon: WarningCircle, label: "Partial" },
    unmapped: { bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle, label: "Not Mapped" },
  };
  const s = map[status] || map.unmapped;
  return (
    <span data-testid={`sb-status-${status}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: s.bg, color: s.fg, fontSize: 11, fontWeight: 800 }}>
      <s.Icon size={13} weight="fill" /> {s.label}
    </span>
  );
}

// Parse a quantity string like "9,200" to a number (NaN when blank/invalid).
const toQty = (v) => { const n = parseFloat(String(v ?? "").replace(/,/g, "")); return Number.isFinite(n) ? n : NaN; };

// Normalize a part/item code for comparison (trim, upper-case, drop internal whitespace).
const normCode = (v) => String(v ?? "").trim().toUpperCase().replace(/\s+/g, "");

// 3-way item match for the Shipping Bill: SB part number  ↔  ERP RMP code (dbo.item.rmpcode)  ↔
// ERP item code (icode). SB lines are grouped by part number and matched to the invoice line(s)
// whose rmpcode OR icode equals that part number; quantities are summed (handles one RMP → many
// icodes). Flags:  match → codes matched & qty agrees · qty → matched but qty differs ·
// sb_only → SB part not on the ERP invoice · erp_only → invoice line with no SB part.
function reconcileItems(sbItems, erpItems) {
  const sb = sbItems || [];
  const erp = (erpItems || []).map((e) => ({ ...e, _used: false }));
  const rows = [];

  // Group SB lines by normalized part number.
  const sbGroups = new Map();
  const sbNoCode = [];
  for (const s of sb) {
    const key = normCode(s.part_code);
    if (!key) { sbNoCode.push(s); continue; }
    const g = sbGroups.get(key) || { part: s.part_code || "", qty: 0, hasQty: false, desc: "" };
    const q = toQty(s.quantity);
    if (Number.isFinite(q)) { g.qty += q; g.hasQty = true; }
    if (!g.desc) g.desc = s.description || "";
    sbGroups.set(key, g);
  }

  for (const [key, g] of sbGroups) {
    const matches = erp.filter((e) => !e._used && (normCode(e.rmpcode) === key || normCode(e.icode) === key));
    matches.forEach((e) => { e._used = true; });
    if (matches.length === 0) {
      rows.push({ kind: "sb_only", rmp: g.part, icode: "", erp_rmp: "", erp_desc: "", sb_desc: g.desc, sb_qty: g.hasQty ? g.qty : "", erp_qty: "" });
      continue;
    }
    const erpQty = matches.reduce((a, e) => { const q = toQty(e.qty); return a + (Number.isFinite(q) ? q : 0); }, 0);
    const kind = (g.hasQty && Math.abs(g.qty - erpQty) < 0.001) ? "match" : "qty";
    rows.push({
      kind,
      rmp: g.part,
      icode: matches.map((e) => e.icode).filter(Boolean).join(", "),
      erp_rmp: [...new Set(matches.map((e) => e.rmpcode).filter(Boolean))].join(", "),
      erp_desc: matches.map((e) => e.description).filter(Boolean).join(" · "),
      sb_desc: g.desc,
      sb_qty: g.hasQty ? g.qty : "",
      erp_qty: erpQty,
    });
  }

  // SB lines that had no part number at all (can't be matched by code).
  for (const s of sbNoCode) {
    rows.push({ kind: "sb_only", rmp: "", icode: "", erp_rmp: "", erp_desc: "", sb_desc: s.description || "", sb_qty: s.quantity || "", erp_qty: "" });
  }
  // Invoice lines not claimed by any SB part number.
  for (const e of erp) {
    if (e._used) continue;
    rows.push({ kind: "erp_only", rmp: "", icode: e.icode || "", erp_rmp: e.rmpcode || "", erp_desc: e.description || "", sb_desc: "", sb_qty: "", erp_qty: e.qty ?? "" });
  }
  return rows;
}

const MATCH_STYLE = {
  match: { bg: "#F0FDF4", chip: "#DCFCE7", fg: "#166534", Icon: CheckCircle, label: "Match" },
  qty: { bg: "#FFFBEB", chip: "#FEF3C7", fg: "#92400E", Icon: WarningCircle, label: "Qty differs" },
  sb_only: { bg: "#FEF2F2", chip: "#FEE2E2", fg: "#991B1B", Icon: XCircle, label: "No ERP line" },
  erp_only: { bg: "#FEF2F2", chip: "#FEE2E2", fg: "#991B1B", Icon: XCircle, label: "No SB line" },
};

function ItemMatchPanel({ items, erpItems, erpInvoice }) {
  const rows = reconcileItems(items, erpItems);
  if (rows.length === 0) return null;
  const counts = rows.reduce((a, r) => { a[r.kind] = (a[r.kind] || 0) + 1; return a; }, {});
  const mismatches = (counts.qty || 0) + (counts.sb_only || 0) + (counts.erp_only || 0);
  const th = { textAlign: "left", padding: "6px 8px", fontSize: 10.5, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.3, borderBottom: "1px solid #E2E8F0" };
  const td = { padding: "6px 8px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };
  return (
    <div data-testid="sb-item-match" style={{ marginBottom: 12, border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, padding: "9px 12px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#0F172A", textTransform: "uppercase", letterSpacing: 0.3 }}>
          3-way match — SB part# ↔ ERP RMP ↔ ERP icode{erpInvoice ? ` · invoice ${erpInvoice}` : ""}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 999, background: "#DCFCE7", color: "#166534" }}>{counts.match || 0} matched</span>
          <span data-testid="sb-item-match-mismatch-count" style={{ fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 999, background: mismatches ? "#FEE2E2" : "#E2E8F0", color: mismatches ? "#991B1B" : "#475569" }}>{mismatches} mismatch{mismatches === 1 ? "" : "es"}</span>
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead><tr>
            <th style={th}>SB Part #</th>
            <th style={th}>ERP RMP Code</th>
            <th style={th}>ERP icode</th>
            <th style={th}>ERP Description</th>
            <th style={{ ...th, textAlign: "right" }}>SB Qty</th>
            <th style={{ ...th, textAlign: "right" }}>ERP Qty</th>
            <th style={{ ...th, textAlign: "center" }}>Status</th>
          </tr></thead>
          <tbody>
            {rows.map((r, i) => {
              const s = MATCH_STYLE[r.kind];
              const qtyHi = r.kind === "qty";
              return (
                <tr key={i} data-testid={`sb-item-match-row-${i}`} style={{ background: s.bg }}>
                  <td style={{ ...td, fontWeight: 700, color: r.kind === "erp_only" ? "#94A3B8" : "#0F172A" }}>{r.rmp || "—"}</td>
                  <td style={{ ...td, fontWeight: 700, color: r.kind === "sb_only" ? "#94A3B8" : "#3730A3" }}>{r.erp_rmp || "—"}</td>
                  <td style={{ ...td, fontWeight: 700, color: r.kind === "sb_only" ? "#94A3B8" : "#0F172A" }}>{r.icode || "—"}</td>
                  <td style={{ ...td, color: "#334155", whiteSpace: "normal", maxWidth: 260 }}>{r.erp_desc || r.sb_desc || "—"}</td>
                  <td style={{ ...td, textAlign: "right", color: qtyHi ? "#B45309" : "#334155", fontWeight: qtyHi ? 800 : 400 }}>{(r.sb_qty ?? "") === "" ? "—" : r.sb_qty}</td>
                  <td style={{ ...td, textAlign: "right", color: qtyHi ? "#B45309" : "#334155", fontWeight: qtyHi ? 800 : 400 }}>{(r.erp_qty ?? "") === "" ? "—" : r.erp_qty}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 999, background: s.chip, color: s.fg, fontSize: 10.5, fontWeight: 800 }}>
                      <s.Icon size={12} weight="fill" /> {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


export default function ShippingBills() {
  const [view, setView] = useState("list"); // "list" | "form"
  const [history, setHistory] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("");
  const [saving, setSaving] = useState(false);

  // form state
  const [editId, setEditId] = useState(null);
  const [fileMeta, setFileMeta] = useState({ file_id: null, file_name: null, file_mime: null });
  const [fields, setFields] = useState({});
  const [containers, setContainers] = useState([]);
  const [items, setItems] = useState([]);
  const [mapping, setMapping] = useState(null);
  const [remapping, setRemapping] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/shipping-bills");
      setHistory(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load history");
    } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(5);
    // Extraction is a single backend call (~15-25s for the AI step), so we drive an animated
    // progress bar with staged labels and cap it at ~92% until the real response returns.
    const stages = [[12, "Uploading PDF…"], [30, "Reading document…"], [58, "AI capturing fields…"], [82, "Mapping to ERP shipments…"]];
    let step = 0;
    setStage(stages[0][1]);
    const timer = setInterval(() => {
      setProgress((p) => {
        const next = Math.min(92, p + Math.max(1, Math.round((92 - p) * 0.07)));
        while (step < stages.length - 1 && next >= stages[step + 1][0]) { step++; setStage(stages[step][1]); }
        return next;
      });
    }, 350);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/shipping-bills/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clearInterval(timer);
      setStage("Done"); setProgress(100);
      const d = r.data || {};
      setEditId(null);
      setFileMeta({ file_id: d.file_id, file_name: d.file_name, file_mime: d.file_mime });
      setFields(d.fields || {});
      setContainers((d.fields?.containers || []).map((c) => ({ container_no: c.container_no || "", seal_no: c.seal_no || "" })));
      setItems(d.fields?.items || []);
      setMapping(d.mapping || null);
      setDupInfo(d.duplicate && d.duplicate.exists ? d.duplicate : null);
      setView("form");
      toast.success(d.used_vision ? "Captured via image OCR — please review" : "Fields captured — please review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      clearInterval(timer);
      setUploading(false);
      setTimeout(() => { setProgress(0); setStage(""); }, 500);
    }
  };

  const openExisting = async (id) => {
    try {
      const r = await api.get(`/shipping-bills/${id}`);
      const d = r.data || {};
      setEditId(d.id);
      setFileMeta({ file_id: d.file_id, file_name: d.file_name, file_mime: d.file_mime });
      setFields(d);
      setContainers((d.containers || []).map((c) => ({ container_no: c.container_no || "", seal_no: c.seal_no || "" })));
      setItems(d.items || []);
      setMapping(d.mapping || null);
      setDupInfo(null);
      setView("form");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open record");
    }
  };

  // Deep-link support: /logistics/shipping-bills?open=<id> auto-opens that record for editing
  // (used by the "Edit in Upload" button on the Shipping Bill Analytics page).
  useEffect(() => {
    const openId = new URLSearchParams(window.location.search).get("open");
    if (openId) openExisting(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const removeRow = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this shipping bill? This cannot be undone.")) return;
    try {
      await api.delete(`/shipping-bills/${id}`);
      toast.success("Shipping bill deleted");
      setHistory((h) => h.filter((r) => r.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const recheckMapping = async () => {
    setRemapping(true);
    try {
      const r = await api.post("/shipping-bills/map", {
        containers,
        export_invoice_number: fields.export_invoice_number || "",
        gst_invoice_number: fields.gst_invoice_number || "",
        contract_no: fields.contract_no || "",
      });
      setMapping(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mapping check failed");
    } finally { setRemapping(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        file_id: fileMeta.file_id, file_name: fileMeta.file_name, file_mime: fileMeta.file_mime,
        fields, containers, items, confidence: fields.confidence,
      };
      const r = editId
        ? await api.put(`/shipping-bills/${editId}`, payload)
        : await api.post("/shipping-bills", payload);
      toast.success(editId ? "Shipping bill updated" : "Shipping bill saved");
      setView("list");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reprocess = async () => {
    if (!editId) return;
    setSaving(true);
    try {
      const r = await api.post(`/shipping-bills/${editId}/reprocess`);
      const d = r.data || {};
      setFields(d);
      setContainers((d.containers || []).map((c) => ({ container_no: c.container_no || "", seal_no: c.seal_no || "" })));
      setItems(d.items || []);
      setMapping(d.mapping || null);
      toast.success("Re-processed from PDF");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-process failed");
    } finally { setSaving(false); }
  };

  const viewPdf = async () => {
    if (!fileMeta.file_id) return;
    try {
      const r = await api.get(`/shipping-bills/file/${fileMeta.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: fileMeta.file_mime || "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open PDF");
    }
  };

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));
  const setContainer = (i, k, v) => setContainers((cs) => cs.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addContainer = () => setContainers((cs) => [...cs, { container_no: "", seal_no: "" }]);
  const rmContainer = (i) => setContainers((cs) => cs.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems((it) => it.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const addItem = () => setItems((it) => [...it, { part_code: "", hs_code: "", description: "", quantity: "", unit: "" }]);
  const rmItem = (i) => setItems((it) => it.filter((_, idx) => idx !== i));

  const containerMap = (cno) => (mapping?.containers || []).find((c) => (c.container_no || "").trim() === (cno || "").trim());

  // ─────────────────────────── LIST VIEW ───────────────────────────
  if (view === "list") {
    return (
      <Layout breadcrumb={["Logistics", "Upload Shipping Bill"]}>
        <div data-testid="shipping-bills-page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {uploading && (
            <div data-testid="sb-progress-overlay" style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
              <div style={{ width: 420, maxWidth: "90vw", background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 20px 50px rgba(2,8,23,0.35)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <CloudArrowUp size={22} weight="bold" color="#0EA5E9" />
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>Processing Shipping Bill</div>
                </div>
                <div style={{ height: 10, background: "#E2E8F0", borderRadius: 999, overflow: "hidden" }}>
                  <div data-testid="sb-progress-bar" style={{ height: "100%", width: `${progress}%`, background: "linear-gradient(90deg,#0EA5E9,#22C55E)", borderRadius: 999, transition: "width 0.35s ease" }} />
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
                  <span data-testid="sb-progress-stage" style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>{stage}</span>
                  <span style={{ fontSize: 13, color: "#64748B", fontWeight: 700 }}>{progress}%</span>
                </div>
                <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>AI is reading the PDF — this can take up to ~25 seconds.</div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Shipping Bills</h1>
              <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>Upload a customs Shipping Bill PDF — we auto-capture the key fields and map them to your shipment data.</p>
            </div>
            <label data-testid="sb-upload-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", background: "#0EA5E9", color: "#fff", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: uploading ? "wait" : "pointer" }}>
              <CloudArrowUp size={18} weight="bold" />
              {uploading ? "Extracting…" : "Upload Shipping Bill (PDF)"}
              <input type="file" accept="application/pdf,.pdf" style={{ display: "none" }} disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onUpload(f); }} />
            </label>
          </div>

          <div style={cardStyle}>
            {loadingList ? (
              <div style={{ padding: 20, color: "#64748B", fontSize: 13 }}>Loading…</div>
            ) : history.length === 0 ? (
              <div data-testid="sb-empty" style={{ padding: 28, textAlign: "center", color: "#94A3B8" }}>
                <FileText size={34} weight="light" />
                <div style={{ marginTop: 8, fontSize: 13 }}>No shipping bills yet. Upload your first PDF above.</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table data-testid="sb-history-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>
                      <th style={{ padding: "8px 10px" }}>SB No.</th>
                      <th style={{ padding: "8px 10px" }}>Exporter</th>
                      <th style={{ padding: "8px 10px" }}>Container(s)</th>
                      <th style={{ padding: "8px 10px" }}>Export Invoice (ERP)</th>
                      <th style={{ padding: "8px 10px" }}>Mapping</th>
                      <th style={{ padding: "8px 10px" }}>Uploaded</th>
                      <th style={{ padding: "8px 10px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h) => (
                      <tr key={h.id} data-testid={`sb-row-${h.id}`} style={{ borderTop: "1px solid #F1F5F9", cursor: "pointer" }} onClick={() => openExisting(h.id)}>
                        <td style={{ padding: "9px 10px", fontWeight: 700, color: "#0F172A" }}>{h.shipping_bill_no || "—"}</td>
                        <td style={{ padding: "9px 10px", color: "#334155" }}>{h.exporter_name || "—"}</td>
                        <td style={{ padding: "9px 10px", color: "#334155" }}>{(h.container_nos || []).join(", ") || "—"}</td>
                        <td style={{ padding: "9px 10px", color: "#334155", fontWeight: 600 }}>{(h.export_invoices || []).join(", ") || "—"}</td>
                        <td style={{ padding: "9px 10px" }}><StatusPill status={h.mapped_status} /></td>
                        <td style={{ padding: "9px 10px", color: "#64748B", fontSize: 12 }}>{h.created_by} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td style={{ padding: "9px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                          <button data-testid={`sb-open-${h.id}`} onClick={(e) => { e.stopPropagation(); openExisting(h.id); }} title="Open" style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}><Eye size={16} weight="bold" color="#0EA5E9" /></button>
                          <button data-testid={`sb-delete-${h.id}`} onClick={(e) => removeRow(h.id, e)} title="Delete" style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}><Trash size={16} weight="bold" color="#EF4444" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // ─────────────────────────── FORM VIEW ───────────────────────────
  // Flag when the SB's printed invoice value differs from the ERP-authoritative one.
  const _ivErp = _num(fields.invoice_value_fc);
  const _ivSb = _num(fields.invoice_value_fc_sb);
  const invoiceMismatch = Number.isFinite(_ivErp) && Number.isFinite(_ivSb) && Math.abs(_ivErp - _ivSb) > 1;
  return (
    <Layout breadcrumb={["Logistics", "Upload Shipping Bill", editId ? "Edit" : "Review"]}>
      <div data-testid="sb-form" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <button data-testid="sb-back-btn" onClick={() => setView("list")} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: "1px solid #CBD5E1", padding: "7px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#334155", cursor: "pointer" }}>
            <ArrowLeft size={15} weight="bold" /> Back
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {mapping && <StatusPill status={mapping.status} />}
            {fileMeta.file_id && (
              <button data-testid="sb-view-pdf" onClick={viewPdf} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0EA5E9", fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>
                <FileText size={15} weight="bold" /> View PDF
              </button>
            )}
            {editId && (
              <button data-testid="sb-reprocess-btn" onClick={reprocess} disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: "#F1F5F9", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving ? "wait" : "pointer" }}>
                <ArrowsClockwise size={15} weight="bold" /> Re-process from PDF
              </button>
            )}
            <button data-testid="sb-save-btn" onClick={save} disabled={saving} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 18px", background: "#16A34A", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: saving ? "wait" : "pointer" }}>
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : (editId ? "Update" : "Save Shipping Bill")}
            </button>
          </div>
        </div>

        {dupInfo && dupInfo.exists && (
          <div data-testid="sb-duplicate-warning" style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#FEF3C7", border: "1px solid #FDE68A", color: "#92400E", borderRadius: 10, padding: "12px 14px" }}>
            <WarningCircle size={20} weight="fill" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13 }}>
              <b>Possible duplicate entry.</b> A shipping bill with SB No. <b>{fields.shipping_bill_no}</b> is already saved
              {dupInfo.count > 1 ? ` (${dupInfo.count} existing records)` : ""}
              {dupInfo.matches?.[0]?.gst_invoice_number ? ` — GST Inv ${dupInfo.matches[0].gst_invoice_number}` : ""}.
              Saving will <b>update the existing record</b> (no duplicate row will be created).
            </div>
          </div>
        )}

        {/* Field groups */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
          {GROUPS.map((g) => (
            <div key={g.title} style={cardStyle}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>{g.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {g.fields.map(([k, lbl]) => (
                  <div key={k} style={{ gridColumn: lbl.length > 24 ? "1 / -1" : "auto" }}>
                    <label style={labelStyle}>{lbl}</label>
                    <input data-testid={`sb-field-${k}`} style={inputStyle} value={fields[k] || ""} onChange={(e) => setField(k, e.target.value)} />
                  </div>
                ))}
              </div>
              {g.title === "Invoices" && fields.export_invoice_sb && (
                <div data-testid="sb-invoice-sb-hint" style={{ marginTop: 10, fontSize: 11, color: "#64748B" }}>
                  On the SB the invoice read as <b>{fields.export_invoice_sb}</b>; the Export Invoice No. above is resolved from the Dispatch Register (authoritative).
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Values window — FC amounts (editable) with computed INR, then FOB/DBK/RODTEP below */}
        <div style={cardStyle} data-testid="sb-values-card">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A" }}>Values</div>
              {invoiceMismatch && (
                <span data-testid="sb-invoice-mismatch-badge" style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 999, background: "#FEE2E2", color: "#991B1B", fontSize: 11, fontWeight: 800 }}>
                  <WarningCircle size={13} weight="fill" /> Invoice value mismatch
                </span>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#64748B" }}>Exchange Rate (INR / FC)</label>
              <input data-testid="sb-field-exchange_rate" style={{ ...inputStyle, width: 120 }} value={fields.exchange_rate || ""} onChange={(e) => setField("exchange_rate", e.target.value)} />
            </div>
          </div>
          {invoiceMismatch && (
            <div data-testid="sb-invoice-mismatch-note" style={{ marginBottom: 12, fontSize: 12, color: "#991B1B", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "9px 12px" }}>
              The Shipping Bill's printed invoice value (<b>{fields.invoice_value_fc_sb}</b>) does not match the ERP invoice value (<b>{fields.invoice_value_fc}</b>). These should always match — please verify the correct invoice/SB before saving.
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {VALUE_ROWS.map((r) => (
              <div key={r.k}>
                <label style={labelStyle}>{r.lbl}</label>
                {r.type === "inr" ? (
                  <input data-testid={`sb-field-${r.k}`} readOnly tabIndex={-1}
                    style={{ ...inputStyle, background: "#F8FAFC", color: "#475569", cursor: "not-allowed" }}
                    value={inrFromFc(fields[r.from], fields.exchange_rate)} placeholder="—" />
                ) : (
                  <input data-testid={`sb-field-${r.k}`} style={r.k === "invoice_value_fc" && invoiceMismatch ? { ...inputStyle, borderColor: "#EF4444", background: "#FEF2F2" } : inputStyle} value={fields[r.k] || ""} onChange={(e) => setField(r.k, e.target.value)} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px dashed #E2E8F0", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {VALUE_EXTRA.map((r) => (
              <div key={r.k}>
                <label style={labelStyle}>{r.lbl}</label>
                <input data-testid={`sb-field-${r.k}`} style={inputStyle} value={fields[r.k] || ""} onChange={(e) => setField(r.k, e.target.value)} />
              </div>
            ))}
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A" }}>Containers &amp; Mapping</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button data-testid="sb-recheck-btn" onClick={recheckMapping} disabled={remapping} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#F1F5F9", border: "1px solid #CBD5E1", padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#334155", cursor: "pointer" }}>
                <ArrowsClockwise size={14} weight="bold" style={remapping ? { animation: "spin 1s linear infinite" } : undefined} /> Re-check Mapping
              </button>
              <button data-testid="sb-add-container-btn" onClick={addContainer} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#EFF6FF", border: "1px solid #BFDBFE", padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#1D4ED8", cursor: "pointer" }}>
                <Plus size={14} weight="bold" /> Add Container
              </button>
            </div>
          </div>
          {containers.length === 0 && <div style={{ color: "#94A3B8", fontSize: 13, padding: "6px 2px" }}>No containers captured. Add one to map.</div>}
          {containers.map((c, i) => {
            const m = containerMap(c.container_no);
            return (
              <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 10, padding: "8px 0", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
                <div style={{ flex: "1 1 200px" }}>
                  <label style={labelStyle}>Container No.</label>
                  <input data-testid={`sb-container-no-${i}`} style={inputStyle} value={c.container_no} onChange={(e) => setContainer(i, "container_no", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={labelStyle}>Seal No.</label>
                  <input data-testid={`sb-container-seal-${i}`} style={inputStyle} value={c.seal_no} onChange={(e) => setContainer(i, "seal_no", e.target.value)} />
                </div>
                <div style={{ flex: "1 1 220px", paddingBottom: 8 }}>
                  {m ? (
                    m.found
                      ? <span data-testid={`sb-container-mapped-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#166534", fontSize: 12, fontWeight: 700 }}><CheckCircle size={14} weight="fill" /> Mapped · Export Inv {m.export_invoice_no || "—"}{m.party ? ` · ${m.party}` : ""}</span>
                      : <span data-testid={`sb-container-unmapped-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#991B1B", fontSize: 12, fontWeight: 700 }}><XCircle size={14} weight="fill" /> Not found in ERP</span>
                  ) : <span style={{ color: "#94A3B8", fontSize: 12 }}>Re-check to map</span>}
                </div>
                <button data-testid={`sb-rm-container-${i}`} onClick={() => rmContainer(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", paddingBottom: 10 }}><Trash size={16} weight="bold" /></button>
              </div>
            );
          })}
          {mapping?.export_invoices?.length > 0 && (
            <div data-testid="sb-export-invoices" style={{ marginTop: 10, padding: "8px 12px", borderRadius: 7, background: "#F0FDF4", fontSize: 12, color: "#166534", fontWeight: 700 }}>
              Export Invoice(s) from ERP: {mapping.export_invoices.join(", ")}
            </div>
          )}
        </div>

        {/* Line items */}
        <div style={cardStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A" }}>Line Items</div>
            <button data-testid="sb-add-item-btn" onClick={addItem} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#EFF6FF", border: "1px solid #BFDBFE", padding: "6px 12px", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#1D4ED8", cursor: "pointer" }}>
              <Plus size={14} weight="bold" /> Add Item
            </button>
          </div>
          {mapping?.erp_items?.length > 0 || items.length > 0 ? (
            <ItemMatchPanel items={items} erpItems={mapping?.erp_items} erpInvoice={mapping?.invoice_match?.erp_invoice} />
          ) : null}
          {items.length === 0 && <div style={{ color: "#94A3B8", fontSize: 13, padding: "6px 2px" }}>No line items captured.</div>}
          {items.map((it, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 8, padding: "8px 0", borderTop: i ? "1px solid #F1F5F9" : "none" }}>
              {[["part_code", "Part/Item Code", 130], ["hs_code", "HS Code", 110], ["description", "Description", 220], ["quantity", "Qty", 90], ["unit", "Unit", 80]].map(([k, lbl, w]) => (
                <div key={k} style={{ flex: `1 1 ${w}px` }}>
                  <label style={labelStyle}>{lbl}</label>
                  <input data-testid={`sb-item-${k}-${i}`} style={inputStyle} value={it[k] || ""} onChange={(e) => setItem(i, k, e.target.value)} />
                </div>
              ))}
              <button data-testid={`sb-rm-item-${i}`} onClick={() => rmItem(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#EF4444", paddingBottom: 10 }}><Trash size={16} weight="bold" /></button>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
